/**
 * Creates a parallax effect on elements with classes that start with "translate-".
 * The function calculates the translation based on the element's position relative to the viewport
 * and applies translation effects based on the specified direction and speed.
 *
 * The effect is responsive and scales down on smaller devices using Bootstrap breakpoints.
 *
 * Usage:
 * 1. Add a class to the element in one of these formats:
 *    - "translate-{direction}-{speed}" for single-axis movement
 *      {direction} can be "up", "down", "left", or "right"
 *    - "translate-{vertical}-{horizontal}-{speed}" for corner-based movement
 *      {vertical} can be "top" or "bottom"
 *      {horizontal} can be "left" or "right"
 *      {speed} is optional for corner movements (defaults to 5)
 * 2. Optionally add "parallax" class for parallax-style effect, or leave without for motion effect.
 * 3. Optionally add disable classes to control animation on different device sizes:
 *    - "disable-mobile-animation" - disables on mobile (xs & sm breakpoints)
 *    - "disable-tablet-animation" - disables on tablets (md breakpoint)
 *    - "disable-mobile-tablet-animation" - disables on both mobile and tablets
 *
 * Examples:
 * <div class="translate-up-5"></div>        <!-- moves up -->
 * <div class="translate-down-3"></div>      <!-- moves down -->
 * <div class="translate-top-right-4"></div> <!-- moves diagonally to top-right -->
 * <div class="translate-bottom-left"></div> <!-- moves diagonally to bottom-left (speed 5) -->
 * <div class="translate-top-right parallax"></div> <!-- parallax-style corner movement -->
 * <div class="translate-up-5 disable-mobile-animation"></div> <!-- moves up but disabled on mobile -->
 * <div class="translate-left-3 disable-tablet-animation"></div> <!-- moves left but disabled on tablets -->
 * <div class="translate-top-right disable-mobile-tablet-animation"></div> <!-- desktop only -->
 *
 * The elements will move in the specified directions when the user scrolls,
 * with reduced movement on smaller screens for better mobile experience.
 *
 * @function createScrollEffects
 */
const createScrollEffects = () => {
	// Select all elements with either parallax or motion class
	const elements = document.querySelectorAll('[class*="translate-"]');

	// Bootstrap breakpoints (in pixels)
	const breakpoints = {
		xs: 0,
		sm: 576,
		md: 768,
		lg: 992,
		xl: 1200,
		xxl: 1400,
	};

	// Scale factor for each breakpoint (smaller screens get reduced effect)
	const breakpointScales = {
		xs: 0.3, // 30% of the effect on extra small screens
		sm: 0.5, // 50% of the effect on small screens
		md: 0.7, // 70% of the effect on medium screens
		lg: 0.85, // 85% of the effect on large screens
		xl: 0.95, // 95% of the effect on extra large screens
		xxl: 1, // 100% of the effect on extra extra large screens
	};

	// Get current breakpoint
	const getCurrentBreakpoint = () => {
		const width = window.innerWidth;
		if (width < breakpoints.sm) return 'xs';
		if (width < breakpoints.md) return 'sm';
		if (width < breakpoints.lg) return 'md';
		if (width < breakpoints.xl) return 'lg';
		if (width < breakpoints.xxl) return 'xl';
		return 'xxl';
	};

	const parseClass = (element) => {
		const classList = Array.from(element.classList);
		const directionalRegex = /translate-(up|down|left|right)-(\d+)/;
		const cornerRegex = /translate-(top|bottom)-(left|right)-?(\d+)?/;

		for (const cls of classList) {
			// Check for corner-based translations first
			const cornerMatch = cls.match(cornerRegex);
			if (cornerMatch) {
				return {
					direction: `${cornerMatch[1]}-${cornerMatch[2]}`, // e.g., "top-right"
					speed: cornerMatch[3] ? parseInt(cornerMatch[3], 10) : 5, // default speed 5
					type: element.classList.contains('parallax')
						? 'parallax'
						: 'motion',
				};
			}

			// Check for directional translations
			const directionalMatch = cls.match(directionalRegex);
			if (directionalMatch) {
				return {
					direction: directionalMatch[1],
					speed: parseInt(directionalMatch[2], 10),
					type: element.classList.contains('parallax')
						? 'parallax'
						: 'motion',
				};
			}
		}
		return null;
	};

	const handleScroll = () => {
		const currentBreakpoint = getCurrentBreakpoint();
		const responsiveScale = breakpointScales[currentBreakpoint];
		const isMobile =
			currentBreakpoint === 'xs' || currentBreakpoint === 'sm';
		const isTablet = currentBreakpoint === 'md';

		elements.forEach((element) => {
			const config = parseClass(element);
			if (!config) return;

			// Check for mobile disable
			if (
				isMobile &&
				element.classList.contains('disable-mobile-animation')
			) {
				element.style.transform = '';
				return;
			}

			// Check for tablet disable
			if (
				isTablet &&
				element.classList.contains('disable-tablet-animation')
			) {
				element.style.transform = '';
				return;
			}

			// Check for mobile + tablet disable
			if (
				(isMobile || isTablet) &&
				element.classList.contains('disable-mobile-tablet-animation')
			) {
				element.style.transform = '';
				return;
			}

			const rect = element.getBoundingClientRect();
			const viewportHeight = window.innerHeight;

			// Skip if element is not in viewport
			if (rect.top > viewportHeight || rect.bottom < 0) {
				return;
			}

			let translateX = 0;
			let translateY = 0;

			if (config.type === 'parallax') {
				// Parallax effect calculation
				const viewportMiddle = viewportHeight / 2;
				const elementMiddle = rect.top + rect.height / 2;
				const distance = elementMiddle - viewportMiddle;
				const translation = distance * (config.speed / 10) * 0.1;

				// Apply responsive scale to the translation
				const scaledTranslation = translation * responsiveScale;

				switch (config.direction) {
					case 'up':
						translateY = -scaledTranslation;
						break;
					case 'down':
						translateY = scaledTranslation;
						break;
					case 'left':
						translateX = -scaledTranslation;
						break;
					case 'right':
						translateX = scaledTranslation;
						break;
					case 'top-left':
						translateY = -scaledTranslation;
						translateX = -scaledTranslation;
						break;
					case 'top-right':
						translateY = -scaledTranslation;
						translateX = scaledTranslation;
						break;
					case 'bottom-left':
						translateY = scaledTranslation;
						translateX = -scaledTranslation;
						break;
					case 'bottom-right':
						translateY = scaledTranslation;
						translateX = scaledTranslation;
						break;
					default:
						translateY = scaledTranslation;
						break;
				}
			} else if (config.type === 'motion') {
				// Motion effect calculation
				const parent = element.parentElement.getBoundingClientRect();
				const scrollY =
					window.scrollY || document.documentElement.scrollTop;
				const startOffset = parent.top + scrollY - viewportHeight;
				const endOffset = parent.bottom + scrollY - viewportHeight;
				const scrollProgress = Math.min(
					1,
					Math.max(
						0,
						1 - (scrollY - startOffset) / (endOffset - startOffset),
					) * 1.5,
				);

				// Apply responsive scale to the distance
				const distance = config.speed * 10 * responsiveScale;

				switch (config.direction) {
					case 'up':
						translateY = scrollProgress * distance;
						break;
					case 'down':
						translateY = -scrollProgress * distance;
						break;
					case 'left':
						translateX = scrollProgress * distance;
						break;
					case 'right':
						translateX = -scrollProgress * distance;
						break;
					case 'top-left':
						translateY = scrollProgress * distance;
						translateX = scrollProgress * distance;
						break;
					case 'top-right':
						translateY = scrollProgress * distance;
						translateX = -scrollProgress * distance;
						break;
					case 'bottom-left':
						translateY = -scrollProgress * distance;
						translateX = scrollProgress * distance;
						break;
					case 'bottom-right':
						translateY = -scrollProgress * distance;
						translateX = -scrollProgress * distance;
						break;
					default:
						break;
				}
			}

			// Apply the transformation
			element.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
		});
	};

	// Add scroll event listener with debounce for better performance
	let scrollTimeout;
	window.addEventListener('scroll', () => {
		if (!scrollTimeout) {
			scrollTimeout = setTimeout(() => {
				handleScroll();
				scrollTimeout = null;
			}, 10); // Small delay for performance
		}
	});

	// Add resize event listener to update when viewport changes
	window.addEventListener('resize', handleScroll);

	// Initial call
	handleScroll();
};

// Initialize the effects
createScrollEffects();
